import React, { useCallback, useMemo, useState } from 'react';
import { Button, IconButton, MenuItem, Text, VSpacer } from '@/components/DesignSystem';
import {
  Box,
  InputAdornment,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  useTheme,
} from '@mui/material';
import Cancel from '@mui/icons-material/Cancel';
import Check from '@mui/icons-material/Check';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { ApiErpCategory } from '@api/interfaces/ApiErpCategory';
import { ErpCategorySelectionModal } from './ErpCategorySelectionModal';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import {
  ErpToIncentive,
} from '@/pages/Admin/HierarchyOfRetailers/CategoryMapping/CategoryMappingModal';
import Edit from '@mui/icons-material/Edit';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { RewardsUom } from '@shared/enums';
import {
  doesMappingMatchNames,
} from '@/pages/Admin/HierarchyOfRetailers/CategoryMapping/helpers';
import { ApiCategory } from '@api/interfaces';
import { ApiRewardsTierSegment } from '@api/interfaces/ApiRewardsTierSegment';

export type IncentiveCategory = { name: string, subcategories: string[] };

interface MapCategoriesStepProps {
  categories: IncentiveCategory[],
  dbCategories: ApiCategory[],
  erpCategories: ApiErpCategory[],
  hasPointsEngineBeenRun?: boolean,
  isEditing?: boolean,
  mappings: ErpToIncentive[],
  onChange: (mappings: ErpToIncentive[]) => void,
  program?: ApiRewardsProgram,
}

type Incentive = { categoryName: string, subcategoryName?: string };

export const MapCategoriesStep = ({
  categories,
  dbCategories,
  erpCategories,
  hasPointsEngineBeenRun = false,
  isEditing = false,
  mappings,
  onChange,
  program,
}: MapCategoriesStepProps) => {
  const { openSnackbar } = useSnackbar();
  const theme = useTheme();
  const [selectedErpCategories, setSelectedErpCategories] = useState<ApiErpCategory[]>([]);
  const [selectedIncentive, setSelectedIncentive] = useState<Incentive | null>(null);
  const [showErpCategorySelectionModal, setShowErpCategorySelectionModal] = useState(false);

  const selectedErpCategoryIds = selectedErpCategories.map(({ id }) => id);
  const disabledErpIds = mappings
    .filter((mapping) => (
      !doesMappingMatchNames(
        mapping,
        selectedIncentive?.categoryName,
        selectedIncentive?.subcategoryName,
      )
    )).map(({ erpCategoryId }) => erpCategoryId);
  const canEdit = isEditing && !hasPointsEngineBeenRun;

  const handleCreateMapping = () => {
    if (!selectedIncentive || !selectedErpCategories.length) {
      return;
    }
    const newMappings = selectedErpCategories.map((erpCategory) => ({
      categoryName: selectedIncentive.categoryName,
      erpCategoryId: erpCategory.id,
      subcategoryName: selectedIncentive.subcategoryName,
    }));
    const existingMappings = mappings.filter((mapping) => (
      !doesMappingMatchNames(
        mapping,
        selectedIncentive.categoryName,
        selectedIncentive.subcategoryName,
      )
    ));
    onChange([...existingMappings, ...newMappings]);
    openSnackbar('Categories mapped');
    setSelectedErpCategories([]);
    setSelectedIncentive(null);
  };

  const handleDeleteMapping = (catName: string, subName?: string | null) => {
    onChange(mappings.filter((mapping) => (
      !doesMappingMatchNames(mapping, catName, subName)
    )));
    openSnackbar('Category deleted');
  };

  const getCategoryDisplayText = useCallback((catName: string, subName?: string | null) => (
    subName
      ? `${catName}: ${subName}`
      : catName
  ), []);

  const erpCategoryMap = useMemo(() => {
    const erpCategoryMap = new Map<string, ApiErpCategory[]>();
    erpCategories.forEach((erpCategory) => {
      if (erpCategory.parent) {
        const parentCategory = erpCategories.find(({ id }) => id === erpCategory.parentId);
        if (parentCategory) {
          const key = `${parentCategory.name}:::${erpCategory.name}`;
          if (!erpCategoryMap.has(key)) {
            erpCategoryMap.set(key, []);
          }
          erpCategoryMap.get(key)?.push(erpCategory);
        }
      } else {
        const key = `${erpCategory.name}:::`;
        if (!erpCategoryMap.has(key)) {
          erpCategoryMap.set(key, []);
        }
        erpCategoryMap.get(key)?.push(erpCategory);
      }
    });
    return erpCategoryMap;
  }, [erpCategories]);

  const uniqueErpCategories = useMemo(() => (
    Array.from(erpCategoryMap.values()).map(
      (categories) => categories[0],
    )
  ), [erpCategoryMap]);

  const groupedMappings = useMemo(() => {
    const sorted = mappings.sort((a, b) => {
      const categoryCompare = a.categoryName.localeCompare(b.categoryName);
      if (categoryCompare !== 0) return categoryCompare;
      const subCompare = (a.subcategoryName ?? '').localeCompare(b.subcategoryName ?? '');
      if (subCompare !== 0) return subCompare;
      const erpCategoryA = uniqueErpCategories.find(({ id }) => id === a.erpCategoryId);
      const erpCategoryB = uniqueErpCategories.find(({ id }) => id === b.erpCategoryId);
      if (!erpCategoryA || !erpCategoryB) return 0;
      return erpCategoryA.name.localeCompare(erpCategoryB.name);
    });
    const groupedMap = new Map<string, ErpToIncentive[]>();
    sorted.forEach((mapping) => {
      const key = `${mapping.categoryName}:::${mapping.subcategoryName ?? ''}`;
      if (!groupedMap.has(key)) {
        groupedMap.set(key, []);
      }
      groupedMap.get(key)?.push(mapping);
    });
    return groupedMap;
  }, [mappings, uniqueErpCategories]);

  const incentiveMap = useMemo(() => {
    const map = new Map<string, Incentive>();

    categories.forEach(({ name: categoryName, subcategories }) => {
      const key = getCategoryDisplayText(categoryName); // parent only
      map.set(key, { categoryName });
      subcategories.forEach((subcategoryName) => {
        const key = getCategoryDisplayText(categoryName, subcategoryName);
        map.set(key, { categoryName, subcategoryName });
      });
    });
    return map;
  }, [categories, getCategoryDisplayText]);

  const selectedIncentiveText = selectedIncentive
    ? getCategoryDisplayText(selectedIncentive.categoryName, selectedIncentive.subcategoryName)
    : '';

  const SelectPlaceholder = (
    <Box
      sx={{
        color: theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
      }}
    >
      Select
    </Box>
  );

  const selectedErpCategoriesText = useMemo(() => {
    const selectedErpCategoriesTexts = [...new Set(
      selectedErpCategories.map(({ parentId, name }) => {
        const parent = erpCategories.find(({ id }) => id === parentId);
        if (parent) {
          return `${parent.name}: ${name}`;
        }
        return name;
      }),
    )];
    selectedErpCategoriesTexts.sort((a, b) => a.localeCompare(b));
    return selectedErpCategoriesTexts.join('; ');
  }, [erpCategories, selectedErpCategories]);

  const isMapButtonDisabled = !selectedIncentive || !selectedErpCategories.length;
  const showUomCategoryWarning = useMemo(() => {
    if (!program?.tiers?.length) {
      return false;
    }
    const uomProgramCategoryIds: string[] = [];
    const segments = program.tiers.flatMap(({ segments }) => segments) as ApiRewardsTierSegment[];
    segments.forEach(({ categoryId, loyaltyUom, subcategoryRewards }) => {
      if (loyaltyUom && loyaltyUom !== RewardsUom.Dollars) {
        uomProgramCategoryIds.push(categoryId);
      }
      if (subcategoryRewards) {
        Object.entries(subcategoryRewards).forEach(([id, rewards]) => {
          if (rewards.loyaltyUom && rewards.loyaltyUom !== RewardsUom.Dollars) {
            uomProgramCategoryIds.push(id);
          }
        });
      }
    });
    const uomProgramCategories = dbCategories.filter(({ id }) => (
      uomProgramCategoryIds.includes(id)
    ));
    const existsMappingToLoyaltyUomCategory = uomProgramCategories.some((category) => {
      const parent = category.parentId
        ? dbCategories.find(({ id }) => id === category.parentId)
        : null;
      const categoryName = parent?.name || category.name;
      const subcategoryName = parent ? category.name : null;
      return mappings.some((mapping) => (
        doesMappingMatchNames(mapping, categoryName, subcategoryName)
      ));
    });
    return existsMappingToLoyaltyUomCategory;
  }, [dbCategories, mappings, program?.tiers]);

  const MappingInputs = (
    <Stack
      alignItems="end"
      direction="row"
      gap="16px"
      justifyContent="space-between"
      width="100%"
    >
      <Stack flex={1} minWidth={0}>
        <Text category="title-small">
          Select incentive category
        </Text>
        <VSpacer size="3"/>
        <Select
          disabled={!canEdit}
          displayEmpty
          endAdornment={!!selectedIncentive && (
            <InputAdornment position="end" sx={{ mr: '28px' }}>
              <IconButton
                onClick={(e) => {
                  setSelectedIncentive(null);
                  e.stopPropagation();
                }}
                testID="incentive-select-clear-button"
              >
                <Cancel />
              </IconButton>
            </InputAdornment>
          )}
          onChange={(e) => {
            const selected = incentiveMap.get(e.target.value as string);
            if (selected) {
              setSelectedIncentive(selected);
              const hasMappings = mappings.some((mapping) => (
                doesMappingMatchNames(mapping, selected.categoryName, selected.subcategoryName)
              ));
              if (!hasMappings && !selectedIncentive) {
                setSelectedErpCategories([]);
              } else {
                setSelectedErpCategories(
                  mappings.filter((mapping) => (
                    doesMappingMatchNames(mapping, selected.categoryName, selected.subcategoryName)
                  )).map(({ erpCategoryId }) => (
                    erpCategories.find(({ id }) => id === erpCategoryId)!
                  )));
              }
            }
          }}
          renderValue={() => selectedIncentiveText || SelectPlaceholder}
          value={selectedIncentiveText}
        >
          {categories
            .sort((a, b) => a.name.localeCompare(b.name))
            .flatMap(({ name, subcategories }) => ([
              <MenuItem
                key={name}
                testID={`category-menu-item-${name}`}
                value={getCategoryDisplayText(name)}
              >
                {getCategoryDisplayText(name)}
              </MenuItem>,
              ...subcategories.map((subcategoryName) => (
                <MenuItem
                  key={`${name}-${subcategoryName}`}
                  testID={`category-menu-item-${name}-${subcategoryName}`}
                  value={getCategoryDisplayText(name, subcategoryName)}
                >
                  {getCategoryDisplayText(name, subcategoryName)}
                </MenuItem>
              )),
            ]))}
        </Select>
      </Stack>
      <Stack flex={1} minWidth={0}>
        <Text category="title-small">
          Select ERP categories
        </Text>
        <VSpacer size="3"/>
        <TextField
          InputProps={{
            endAdornment: !!selectedErpCategoriesText && (
              <InputAdornment position="end" sx={{ mr: '28px' }}>
                <IconButton
                  onClick={(e) => {
                    setSelectedErpCategories([]);
                    e.stopPropagation();
                  }}
                  testID="erp-category-select-clear-button"
                >
                  <Cancel />
                </IconButton>
              </InputAdornment>
            ),
          }}
          SelectProps={{
            displayEmpty: true,
            open: false,
            renderValue: () => selectedErpCategoriesText ? (
              <Box
                alignItems="center"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                width="120%"
              >
                {selectedErpCategoriesText}
              </Box>
            ) : SelectPlaceholder,
          }}
          disabled={!canEdit}
          onClick={(e) => {
            if (canEdit) {
              setShowErpCategorySelectionModal(true);
            }
            e.stopPropagation();
          }}
          select
          value={selectedErpCategoriesText}
        >
          <MenuItem testID="erp-category-text-menu-item" value={selectedErpCategoriesText} />
        </TextField>
      </Stack>
      <Button
        disabled={isMapButtonDisabled}
        onClick={handleCreateMapping}
        startIcon={<Check />}
        sx={{ mb: '8px' }}
        testID="map-categories-button"
      >
        Map
      </Button>
    </Stack>
  );

  const MappingTable = (
    <Table
      sx={{ borderTop: '1px solid', borderColor: theme.palette.outline, tableLayout: 'auto' }}
    >
      <TableHead>
        <TableRow sx={{ height: '52px' }}>
          <TableCell width="30%">
            Incentive category
          </TableCell>
          <TableCell width="55%">
            ERP categories
          </TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from(groupedMappings.entries()).map(([key, mappings]) => {
          const erpIds = mappings.map(({ erpCategoryId }) => erpCategoryId);
          const mappedErpCategories = erpCategories.filter(({ id }) => erpIds.includes(id));
          let erpCategoryTexts: string[] = [];
          mappedErpCategories.forEach((erpCategory) => {
            if (erpCategory.parentId) {
              const parentCategory = erpCategories.find(({ id }) => id === erpCategory.parentId)!;
              erpCategoryTexts.push(getCategoryDisplayText(parentCategory.name, erpCategory.name));
            } else {
              erpCategoryTexts.push(erpCategory.name);
            }
          });
          erpCategoryTexts = [...new Set(erpCategoryTexts)];
          erpCategoryTexts.sort((a, b) => a.localeCompare(b));
          const erpCategoryText = [...erpCategoryTexts].join('; ');
          const [categoryName, subcategoryName] = key.split(':::');
          return (
            <TableRow key={`${key}`} sx={{ height: '52px' }}>
              <TableCell sx={{ py: 0 }} width="30%">
                {getCategoryDisplayText(categoryName, subcategoryName)}
              </TableCell>
              <TableCell sx={{ py: '12px' }} width="55%">
                {erpCategoryText}
              </TableCell>
              <TableCell align="right" sx={{ py: 0 }}>
                {canEdit && (
                  <>
                    <IconButton
                      onClick={() => {
                        setSelectedIncentive({ categoryName, subcategoryName });
                        setSelectedErpCategories(
                          mappings.filter((mapping) => (
                            doesMappingMatchNames(mapping, categoryName, subcategoryName)
                          )).map(({ erpCategoryId }) => (
                            erpCategories.find(({ id }) => id === erpCategoryId)!
                          )));
                      }}
                      testID={`${key}-edit-button`}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteMapping(categoryName, subcategoryName || null)}
                      testID={`${key}-delete-button`}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <>
      <Stack alignItems="center" maxWidth="840px" width="100%">
        <Text category="headline-large">
          Step 2/2: Map Categories*
        </Text>
        <VSpacer size="3" />
        <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
          *The term 'Categories' in this workflow includes subcategories
        </Text>
        <VSpacer size="9" />
        {MappingInputs}
        {showUomCategoryWarning && (
          <>
            <VSpacer size="5" />
            <Text
              category="body-medium"
              color="error"
            >
              ERP categories that are mapped to an incentive category with a non-dollar loyalty
              value will not receive points if they are not sold by that same UOM.
            </Text>
          </>
        )}
        <VSpacer size="7" />
        {MappingTable}
      </Stack>
      {showErpCategorySelectionModal && (
        <ErpCategorySelectionModal
          disabledIds={disabledErpIds}
          erpCategories={uniqueErpCategories}
          onChange={(selectedIds) => {
            const selectedCategories = selectedIds.flatMap((id) => {
              const category = erpCategories.find((category) => category.id === id)!;
              const parent = erpCategories.find((erpCat) => erpCat.id === category.parentId);
              const key = parent ? `${parent.name}:::${category.name}` : `${category.name}:::`;
              return erpCategoryMap.get(key) || [];
            });

            setSelectedErpCategories(selectedCategories);
          }}
          onClose={() => setShowErpCategorySelectionModal(false)}
          selectedIds={selectedErpCategoryIds.filter((id) => (
            uniqueErpCategories.some((uniqueErpCategory) => uniqueErpCategory.id === id)
          ))}
        />
      )}
    </>
  );
};
